import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ActivityIcon } from '../../assets/icons/activety-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calender.svg';
// import { ReactComponent as CarIcon } from '../../assets/icons/car.svg';
// import { ReactComponent as GraphIcon } from '../../assets/icons/chart-icon.svg';
import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as CurrencyIcon } from '../../assets/icons/currentPrice.svg';
import { ReactComponent as LightningIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as HistoryIcon } from '../../assets/icons/history-icon.svg';
// import { ReactComponent as UserIcon } from '../../assets/icons/user-icon.svg';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import powerBankPreauthApi from '../../utils/api/v2/powerbank-preauth';
import TabToggle from '../tab-toggle/TabToggle';

interface PowerBankPreauthSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

/**
 * Formats ISO date string to "DD MMM YYYY, HH:MM AM/PM" format
 * @param {string | undefined | null} dateString - The ISO date string
 * @returns {string} The formatted date string
 */
const formatDateTime = (dateString: string | undefined | null): string => {
  if (!dateString) return '—';
  try {
    const dateValue = ConvertUTCtoDate(dateString);
    const timeValue = FormatUtcString(ConvertUTCtoHHMM(dateString));
    if (dateValue && timeValue) {
      return `${dateValue}, ${timeValue}`;
    }
  } catch (error) {
    console.error(error);
  }
  return dateString;
};

/**
 * PowerBankPreauthSidePanel component to display detail card information for a PowerBank Preauth.
 * @param {PowerBankPreauthSidePanelProperties} properties - Component properties.
 * @returns {any} The rendered side panel.
 */
function PowerBankPreauthSidePanel(properties: PowerBankPreauthSidePanelProperties) {
  const { t } = useTranslation('sidePanel');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const [preauthData, setPreauthData] = useState<any>();
  const [activeTab, setActiveTab] = useState<'session' | 'user'>('session');

  /**
   * Helper function to get nested values from preauthData using dot notation
   * @param {string} path - dot-separated key path
   * @returns {any} value or fallback string
   */
  const getNestedValue = (path: string): any => {
    let nestedValue: any = preauthData;
    try {
      const parts = path.split('.');
      parts.forEach((part) => {
        if (nestedValue !== null && nestedValue !== undefined) {
          const snakePart = part.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
          const resolvedKey = [part, snakePart].find(
            (key) => nestedValue[key] !== undefined && nestedValue[key] !== null,
          );
          nestedValue = resolvedKey !== undefined ? nestedValue[resolvedKey] : undefined;
        }
      });
    } catch (error) {
      console.error(error);
    }
    return nestedValue !== undefined && nestedValue !== null && nestedValue !== ''
      ? nestedValue
      : '—';
  };

  /**
   * Function to fetch PowerBank Preauth details
   * @param {string} preauthId - The preauth ID
   * @returns {Promise<void>} Resolves when fetch completes
   */
  const handleFetchPreauthDetails = async (preauthId: string) => {
    powerBankPreauthApi
      .fetchPreauthDetails(preauthId)
      .then((response) => {
        setPreauthData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Failed to fetch preauth details',
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  useEffect(() => {
    let preauthId;
    if (fetchIdFrom === 'queryParams') {
      preauthId = searchParameters.get('preauthId') || searchParameters.get('id');
    } else if (fetchIdFrom === 'props') {
      preauthId = id;
    }

    if (!preauthId) {
      return;
    }

    setActiveTab('session');
    handleFetchPreauthDetails(preauthId);
  }, [searchParameters, id]);

  const statusValue = getNestedValue('status');

  const currencyValue = getNestedValue('currency') !== '—' ? getNestedValue('currency') : 'SAR';

  const amountValue = getNestedValue('amount');
  const formattedAmount =
    amountValue !== '—' ? `${ToFixedValue(Number(amountValue), 2)} ${currencyValue}` : '—';

  const capturedAmountValue = getNestedValue('capturedAmount');
  const formattedCapturedAmount =
    capturedAmountValue !== '—' && capturedAmountValue !== null
      ? `${ToFixedValue(Number(capturedAmountValue), 2)} ${currencyValue}`
      : '—';

  const preauthInfoList = [
    {
      label: t('referenceId', { defaultValue: 'Reference ID' }),
      value: getNestedValue('internalRef'),
    },
    {
      label: t('pspPreauthId', { defaultValue: 'PSP Preauth ID' }),
      value: getNestedValue('pspPreauthId'),
    },
    {
      label: t('paymentMethod', { defaultValue: 'Payment Method' }),
      value: getNestedValue('paymentMethod'),
    },
    {
      label: t('tokenUid', { defaultValue: 'Token UID' }),
      value: getNestedValue('tokenUid'),
    },
    {
      label: t('amount', { defaultValue: 'Amount' }),
      value: formattedAmount,
    },
    {
      label: t('capturedAmount', { defaultValue: 'Captured Amount' }),
      value: formattedCapturedAmount,
    },
    {
      label: t('status', { defaultValue: 'Status' }),
      value: statusValue,
      isBadge: true,
    },
    {
      label: t('gatewayTransactionId', { defaultValue: 'Gateway TX ID' }),
      value: getNestedValue('gatewayTransactionId'),
    },
  ];

  const chargingInfoList = [
    {
      label: t('cpoSessionId', { defaultValue: 'CPO Session ID' }),
      value: getNestedValue('cpoSessionId'),
    },
    {
      label: t('commandId', { defaultValue: 'Command ID' }),
      value: getNestedValue('commandId'),
    },
    {
      label: t('locationId', { defaultValue: 'Location ID' }),
      value: getNestedValue('locationId'),
    },
    {
      label: t('evseUid', { defaultValue: 'EVSE UID' }),
      value: getNestedValue('evseUid'),
    },
    {
      label: t('connectorId', { defaultValue: 'Connector ID' }),
      value: getNestedValue('connectorId'),
    },
  ];

  const timestampList = [
    {
      label: t('linkedAt', { defaultValue: 'Linked At' }),
      value: formatDateTime(preauthData?.linkedAt),
    },
    {
      label: t('capturedAt', { defaultValue: 'Captured At' }),
      value: formatDateTime(preauthData?.capturedAt),
    },
    {
      label: t('releasedAt', { defaultValue: 'Released At' }),
      value: formatDateTime(preauthData?.releasedAt),
    },
    {
      label: t('orphanedAt', { defaultValue: 'Orphaned At' }),
      value: formatDateTime(preauthData?.orphanedAt),
    },
    {
      label: t('expiresAt', { defaultValue: 'Expires At' }),
      value: formatDateTime(preauthData?.expiresAt),
    },
    {
      label: t('createdAt', { defaultValue: 'Created At' }),
      value: formatDateTime(preauthData?.createdAt),
    },
    {
      label: t('updatedAt', { defaultValue: 'Updated At' }),
      value: formatDateTime(preauthData?.updatedAt),
    },
  ];

  const sessionSections = [
    {
      header: t('preauthInformation', { defaultValue: 'Preauth Information' }),
      icon: <CalendarIcon />,
      items: preauthInfoList,
      classKey: 'session-info',
      tab: 'session',
    },
    {
      header: t('chargingInformation', { defaultValue: 'Charging Information' }),
      icon: <LightningIcon />,
      items: chargingInfoList,
      classKey: 'vehicle-info',
      tab: 'session',
    },
    {
      header: t('timestamps', { defaultValue: 'Timestamps' }),
      icon: <ClockIcon />,
      items: timestampList,
      classKey: 'timestamps',
      tab: 'session',
    },
  ];

  const activeSections = sessionSections.filter((section) => section.tab === activeTab);

  return (
    <div className="emsp-session-side-panel-wrap">
      {/* Scrollable Middle Content */}
      <div className="panel-scrollable-content">
        {/* 4-Metric summary row */}
        <div className="metrics-summary-row">
          {/* Card 1: Status */}
          <div
            className="metric-card"
            title={statusValue ? statusValue.charAt(0).toUpperCase() + statusValue.slice(1) : ''}>
            <div
              className={`badge-icon-wrap status status-${String(statusValue).toLowerCase().replace(/\s+/g, '-')}`}>
              {['released', 'captured', 'orphaned'].includes(String(statusValue).toLowerCase()) ? (
                <HistoryIcon />
              ) : (
                <ActivityIcon />
              )}
            </div>
            <div className="card-info">
              <span className="card-title">{t('statusLabel', { defaultValue: 'Status' })}</span>
              <span
                className={`card-value status-${String(statusValue).toLowerCase().replace(/\s+/g, '-')}`}
                title={
                  statusValue ? statusValue.charAt(0).toUpperCase() + statusValue.slice(1) : ''
                }>
                {statusValue !== '—' ? statusValue.charAt(0).toUpperCase() + statusValue.slice(1) : '—'}
              </span>
            </div>
          </div>

          {/* Card 2: Amount */}
          <div className="metric-card" title={formattedAmount}>
            <div className="badge-icon-wrap currency">
              <CurrencyIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('amount', { defaultValue: 'Amount' })}</span>
              <span className="card-value" title={formattedAmount}>
                {formattedAmount}
              </span>
              <span className="card-subtext" title={t('preauthAmount', { defaultValue: 'Preauth Amount' })}>
                {t('preauthAmount', { defaultValue: 'Preauth Amount' })}
              </span>
            </div>
          </div>

          {/* Card 3: Captured Amount */}
          <div className="metric-card" title={formattedCapturedAmount}>
            <div className="badge-icon-wrap energy">
              <CurrencyIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('captured', { defaultValue: 'Captured' })}</span>
              <span className="card-value" title={formattedCapturedAmount}>
                {formattedCapturedAmount}
              </span>
              <span className="card-subtext" title={t('capturedAmount', { defaultValue: 'Captured Amount' })}>
                {t('capturedAmount', { defaultValue: 'Captured Amount' })}
              </span>
            </div>
          </div>
          
          {/* Card 4: Date */}
          <div className="metric-card" title={formatDateTime(preauthData?.createdAt)}>
            <div className="badge-icon-wrap duration">
              <CalendarIcon />
            </div>
            <div className="card-info">
              <span className="card-title">{t('date', { defaultValue: 'Date' })}</span>
              <span className="card-value" title={formatDateTime(preauthData?.createdAt)}>
                {formatDateTime(preauthData?.createdAt) === '—' ? '—' : ConvertUTCtoDate(preauthData?.createdAt) || '—'}
              </span>
              <span className="card-subtext" title={t('createdDate', { defaultValue: 'Created Date' })}>
                {t('createdDate', { defaultValue: 'Created Date' })}
              </span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="panel-tab-toggle-wrap">
          <TabToggle
            tabs={[
              {
                label: t('preauthDetails', { defaultValue: 'Preauth Details' }),
                value: 'session',
              },
            ]}
            activeTab={activeTab}
            defaultTab="session"
            isLabelValue
            onChange={(value) => setActiveTab(value as 'session' | 'user')}
          />
        </div>

        {/* Details Sections */}
        {activeSections.map((section, index) => (
          <div className="detail-section-card" key={index}>
            <div className={`card-header ${section.classKey}`}>
              {section.icon}
              <h3>{section.header}</h3>
            </div>
            <div className="card-body-grid">
              {section.items.map((item: any, itemIndex: number) => (
                <div className="grid-item" key={itemIndex}>
                  <span className="item-label">{item.label}</span>
                  {item.isBadge ? (
                    <span
                      className={`status-pill ${String(item.value).toLowerCase().replace(/\s+/g, '-')}`}>
                      {item.value}
                    </span>
                  ) : (
                    <span className="item-value">{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PowerBankPreauthSidePanel;
